import { z } from 'zod';
import { LeadLifecycleStatus } from '@/models/entities/lead';
import Actions, { ActionSchema } from '@/models/ui/actions.ts';

export const StepLabelBrand = z.string().brand<'StepLabel'>();
export type StepLabel = z.infer<typeof StepLabelBrand>;

export const StepSchema = z
  .object({
    label: StepLabelBrand,
    primaryAction: ActionSchema,
    secondaryActions: z.array(ActionSchema),
    order: z.number().int().min(1),
    isLast: z.boolean(),
  })
  .transform((original) => ({
    ...original,
    url: original.label.toLowerCase().replaceAll(' ', '-'),
  }))

  .readonly();

export type Step = z.infer<typeof StepSchema>;

export const StepStateSchema = z
  .object({
    step: StepSchema,
    completed: z.boolean(),
  })
  .readonly();

export const Steps = {
  Preparation: StepSchema.parse({
    label: StepLabelBrand.parse('Preparation'),
    primaryAction: Actions.ScheduleAppointment,
    secondaryActions: [Actions.MarkUnreachable],
    order: 1,
    isLast: false,
  }),
  Scheduled: StepSchema.parse({
    label: StepLabelBrand.parse('Scheduled'),
    primaryAction: Actions.MarkInRepair,
    secondaryActions: [Actions.MarkNoShow],
    order: 2,
    isLast: false,
  }),
  InRepair: StepSchema.parse({
    label: StepLabelBrand.parse('In Repair'),
    primaryAction: Actions.MarkAsExecuted,
    secondaryActions: [],
    order: 3,
    isLast: false,
  }),
  Executed: StepSchema.parse({
    label: StepLabelBrand.parse('Executed'),
    primaryAction: Actions.Archive,
    secondaryActions: [],
    order: 4,
    isLast: true,
  }),
} as const;
export const StepsModelSchema = z
  .object({
    steps: z.array(StepStateSchema),
    currentStep: StepSchema,
  })
  .readonly();

export type StepsModel = z.infer<typeof StepsModelSchema>;

export const mapStatus = (status: LeadLifecycleStatus): Step => {
  switch (status) {
    case LeadLifecycleStatus.Lead:
      return Steps.Preparation;
    case LeadLifecycleStatus.Scheduled:
      return Steps.Scheduled;
    case LeadLifecycleStatus.InRepair:
      return Steps.InRepair;
    case LeadLifecycleStatus.Executed:
      return Steps.Executed;
    case LeadLifecycleStatus.Archived:
      throw new Error('Cannot create steps model for an Archived lead');
    default:
      throw new Error(
        `Lead status: ${status} is not mapped to a stepper step.`,
      );
  }
};

const isCompleted = (current: Step, step: Step): boolean =>
  step.order <= current.order;

export const createStepsModel = (status: LeadLifecycleStatus): StepsModel => {
  const currentStep = mapStatus(status);
  const steps = Object.values(Steps).map((step) => ({
    step,
    completed: isCompleted(currentStep, step),
  }));

  return {
    steps,
    currentStep: currentStep,
  };
};
